import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { X, Loader2, ChevronLeft, ChevronRight, FileSpreadsheet, Search, ChevronDown } from "lucide-react";
import api, { baseURL } from "~/services/api";
import { toast } from "react-toastify";
import { saveAs } from "file-saver";

const USDollar = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

const DEBOUNCE_MS = 450;

export default function DefaultRateModal({ isOpen, onClose, periodData }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [isMobile, setIsMobile] = useState(typeof window !== "undefined" ? window.innerWidth < 768 : false);
  const [exportingExcel, setExportingExcel] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedTerm, setDebouncedTerm] = useState("");
  const [activeTab, setActiveTab] = useState("receivable"); // "receivable" | "partial_paid"
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const exportMenuRef = useRef(null);
  const limit = 10;
  const filial = useSelector((state) => state.auth?.filial);
  const showBranchColumn = filial?.id === 1;
  const currentBranchName = filial?.name;

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedTerm((searchTerm || "").trim()), DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [searchTerm]);

  useEffect(() => {
    if (isOpen && periodData) {
      setCurrentPage(1);
      setSearchTerm("");
      setDebouncedTerm("");
      setActiveTab("receivable");
      loadDetailData();
    }
  }, [isOpen, periodData]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedTerm, activeTab]);

  useEffect(() => {
    if (!exportMenuOpen) return;
    const handleClickOutside = (e) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(e.target)) {
        setExportMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [exportMenuOpen]);

  async function loadDetailData() {
    if (!periodData) return;
    setLoading(true);
    try {
      const params = {
        period_from: periodData.from,
        period_to: periodData.to,
        period_by: "Due Date",
        orderBy: "due_date",
        orderASC: "ASC",
        _t: Date.now(),
      };
      const response = await api.get("/reports/default-rate/detail", { params });
      setData(response.data);
    } catch (err) {
      console.error("Error loading default rate detail:", err);
      toast(err?.response?.data?.error || "Error loading detail data", {
        type: "error",
        autoClose: 3000,
      });
    } finally {
      setLoading(false);
    }
  }

  async function handleExportExcel(sheet = "both") {
    if (!periodData) return;
    setExportMenuOpen(false);
    setExportingExcel(true);
    try {
      const response = await api.get("/reports/default-rate/detail/excel", {
        params: {
          period_from: periodData.from,
          period_to: periodData.to,
          period_by: "Due Date",
          sheet,
        },
      });
      const { name } = response.data;
      saveAs(`${baseURL}/get-file/${name}`, name);
      toast("Excel file downloaded successfully!", {
        type: "success",
        autoClose: 3000,
      });
    } catch (err) {
      console.error("Error exporting to Excel:", err);
      toast(err?.response?.data?.error || "Error exporting to Excel", {
        type: "error",
        autoClose: 3000,
      });
    } finally {
      setExportingExcel(false);
    }
  }

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleSearchInputChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    
    if (dateStr.includes("-")) {
      const [year, month, day] = dateStr.split("-");
      return `${month}/${day}/${year}`;
    }
    
    const year = dateStr.substring(0, 4);
    const month = dateStr.substring(4, 6);
    const day = dateStr.substring(6, 8);
    return `${month}/${day}/${year}`;
  };

  const normalized = (s) =>
    (s || "")
      .toString()
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");

  const activeRows = activeTab === "partial_paid" ? data?.partialPaidRows : data?.rows;
  const activeTotals = activeTab === "partial_paid" ? data?.partialPaid?.totals : data?.totals;

  const clientFilteredRows = useMemo(() => {
    if (!activeRows) return [];
    const term = normalized(debouncedTerm);
    if (!term) return activeRows;
    return activeRows.filter((row) => {
      const invoice = normalized(row.invoice_number);
      const issuer = normalized(row.issuer?.name);
      const branch = normalized(row.filial?.name);
      return invoice.includes(term) || issuer.includes(term) || (showBranchColumn && branch.includes(term));
    });
  }, [activeRows, debouncedTerm, showBranchColumn]);

  const totalRowsUI = clientFilteredRows.length;
  const totalPages = totalRowsUI ? Math.ceil(totalRowsUI / limit) + 1 : 1; // +1 for summary page

  const pageSlice = useMemo(() => {
    const start = (currentPage - 1) * limit;
    return clientFilteredRows.slice(start, start + limit);
  }, [clientFilteredRows, currentPage]);

  const startRecord = totalRowsUI ? (currentPage - 1) * limit + 1 : 0;
  const endRecord = totalRowsUI ? Math.min(currentPage * limit, totalRowsUI) : 0;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className={`bg-white rounded-lg shadow-xl ${isMobile ? "w-full h-full" : "w-full max-w-7xl max-h-[90vh]"} flex flex-col`}>
        <div className="flex items-center justify-between p-4 border-b">
          <div>
            <h2 className={`${isMobile ? "text-lg" : "text-xl"} font-bold`}>Delinquency Details - {periodData?.period}</h2>
            {data?.period && (
              <p className="text-sm text-gray-600 mt-1">
                {Number(data.period.rate ?? 0).toFixed(2)}% delinquency rate • {USDollar.format(data.period.overdue || 0)} overdue of {USDollar.format(data.period.total || 0)}
              </p>
            )}
            {data?.period && (
              <p className="text-xs text-gray-500 mt-0.5">
                With fees: {USDollar.format(data.period.overdue_gross || 0)} (includes {USDollar.format(data.period.overdue_fee || 0)} in fees)
              </p>
            )}
            {!showBranchColumn && currentBranchName && (
              <p className="text-xs text-gray-500 mt-1">Branch: {currentBranchName}</p>
            )}
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <X size={24} />
          </button>
        </div>

        <div className="px-4 pt-3 border-b bg-white">
          <div className="flex gap-1">
            <button
              onClick={() => setActiveTab("receivable")}
              className={`px-4 py-2 text-sm font-medium rounded-t-lg border border-b-0 transition-colors ${
                activeTab === "receivable"
                  ? "bg-white border-gray-300 text-green-700 relative top-px"
                  : "bg-gray-100 border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              Pending {data?.totalRows != null ? `(${data.totalRows})` : ""}
            </button>
            <button
              onClick={() => setActiveTab("partial_paid")}
              className={`px-4 py-2 text-sm font-medium rounded-t-lg border border-b-0 transition-colors ${
                activeTab === "partial_paid"
                  ? "bg-white border-gray-300 text-green-700 relative top-px"
                  : "bg-gray-100 border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              Partial Paid {data?.partialPaid?.totalRows != null ? `(${data.partialPaid.totalRows})` : ""}
            </button>
            {data?.breakdown && (
              <button
                onClick={() => setActiveTab("breakdown")}
                className={`px-4 py-2 text-sm font-medium rounded-t-lg border border-b-0 transition-colors ${
                  activeTab === "breakdown"
                    ? "bg-white border-gray-300 text-green-700 relative top-px"
                    : "bg-gray-100 border-transparent text-gray-500 hover:text-gray-700"
                }`}
              >
                Overdue Breakdown
              </button>
            )}
          </div>
        </div>

        {activeTab !== "breakdown" && (
        <div className="p-4 border-b bg-gray-50">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3 text-sm text-gray-700">
              {totalRowsUI > 0 && (
                <>
                  <span>
                    Showing <span className="font-semibold">{startRecord}</span> to <span className="font-semibold">{endRecord}</span> of <span className="font-semibold">{totalRowsUI}</span> rows
                  </span>
                  <span className="text-gray-400">|</span>
                  <span>Page</span>
                  <select
                    value={currentPage}
                    onChange={(e) => handlePageChange(Number(e.target.value))}
                    className="border border-gray-300 rounded-sm px-2 py-1 text-sm focus:outline-hidden focus:ring-2 focus:ring-green-500"
                    disabled={loading || totalPages <= 1}
                  >
                    {Array.from({ length: totalPages || 1 }, (_, i) => i + 1).map((page) => (
                      <option key={page} value={page}>
                        {page}
                      </option>
                    ))}
                  </select>
                  <span>of {totalPages}</span>
                  <button
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={loading || !data}
                    className="ml-2 px-3 py-1 bg-sky-700 text-white text-xs rounded-lg hover:bg-sky-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Summary
                  </button>
                </>
              )}
            </div>

            <div className="flex items-center gap-2 w-full sm:w-auto">
              <div className="relative flex-1 sm:flex-initial">
                <div className="relative">
                  <input
                    type="text"
                    placeholder={showBranchColumn ? "Search by invoice, issuer, branch..." : "Search by invoice or issuer..."}
                    value={searchTerm}
                    onChange={handleSearchInputChange}
                    className="w-full sm:w-80 pl-10 pr-10 py-2 border border-gray-300 rounded-lg text-sm focus:outline-hidden focus:ring-2 focus:ring-green-500"
                  />
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                  {searchTerm && (
                    <button onClick={handleClearSearch} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                      <X size={18} />
                    </button>
                  )}
                </div>
              </div>
              <div className="relative" ref={exportMenuRef}>
                <button
                  onClick={() => setExportMenuOpen((v) => !v)}
                  disabled={exportingExcel || !data}
                  className="flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {exportingExcel ? <Loader2 className="animate-spin" size={18} /> : <FileSpreadsheet size={18} />}
                  <span>Excel</span>
                  <ChevronDown size={16} />
                </button>
                {exportMenuOpen && (
                  <div className="absolute right-0 mt-1 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-10 overflow-hidden">
                    <button
                      onClick={() => handleExportExcel("receivable")}
                      className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                    >
                      Pending only
                    </button>
                    <button
                      onClick={() => handleExportExcel("partial_paid")}
                      className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                    >
                      Partial Paid only
                    </button>
                    <button
                      onClick={() => handleExportExcel("both")}
                      className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                    >
                      Both (Pending + Partial Paid)
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        )}

        <div className="flex-1 overflow-auto">
          {activeTab === "breakdown" ? (
            data?.breakdown && (
              <OverdueBreakdown breakdown={data.breakdown} overdue={data.period?.overdue} />
            )
          ) : loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="animate-spin" size={36} />
            </div>
          ) : currentPage === totalPages && activeTotals ? (
            <div className="overflow-x-auto">
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-800 mb-6">
                  Summary Totals {activeTab === "partial_paid" ? "— Partial Paid" : "— Pending"}
                </h3>
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-blue-100 border-b-2 border-blue-300">
                      <th className="text-left p-4 text-sm font-semibold text-gray-700">Metric</th>
                      <th className="text-right p-4 text-sm font-semibold text-gray-700">Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b hover:bg-blue-50 transition-colors bg-white">
                      <td className="p-4 text-sm font-medium text-gray-900">Amount</td>
                      <td className="p-4 text-sm text-right text-gray-900 font-semibold">{USDollar.format(activeTotals.amount || 0)}</td>
                    </tr>
                    <tr className="border-b hover:bg-blue-50 transition-colors bg-gray-50">
                      <td className="p-4 text-sm font-medium text-gray-900">Discount</td>
                      <td className="p-4 text-sm text-right text-red-600 font-semibold">{activeTotals.discount ? `(${USDollar.format(activeTotals.discount)})` : "-"}</td>
                    </tr>
                    <tr className="border-b-2 border-blue-300 hover:bg-blue-50 transition-colors bg-blue-50">
                      <td className="p-4 text-sm font-bold text-gray-900">Net</td>
                      <td className="p-4 text-right text-blue-700 font-bold text-lg">{USDollar.format(activeTotals.net || 0)}</td>
                    </tr>
                    <tr className="border-b hover:bg-blue-50 transition-colors bg-white">
                      <td className="p-4 text-sm font-medium text-gray-900">Fee</td>
                      <td className="p-4 text-sm text-right text-gray-900 font-semibold">{activeTotals.fee ? USDollar.format(activeTotals.fee) : "-"}</td>
                    </tr>
                    {activeTab === "partial_paid" && (
                      <tr className="border-b hover:bg-blue-50 transition-colors bg-gray-50">
                        <td className="p-4 text-sm font-medium text-gray-900">Paid</td>
                        <td className="p-4 text-sm text-right text-green-700 font-semibold">{USDollar.format(activeTotals.paid || 0)}</td>
                      </tr>
                    )}
                    <tr className="border-b hover:bg-blue-50 transition-colors bg-white">
                      <td className="p-4 text-sm font-medium text-gray-900">Total</td>
                      <td className="p-4 text-sm text-right text-gray-900 font-bold text-lg">{USDollar.format(activeTotals.total || 0)}</td>
                    </tr>
                    <tr className="border-b hover:bg-blue-50 transition-colors bg-gray-50">
                      <td className="p-4 text-sm font-medium text-gray-900">Net Balance</td>
                      <td className="p-4 text-sm text-right text-blue-600 font-bold text-lg">{USDollar.format(activeTotals.net_balance ?? activeTotals.balance ?? 0)}</td>
                    </tr>
                    {activeTotals.fee_balance != null && (
                      <tr className="border-b hover:bg-blue-50 transition-colors bg-white">
                        <td className="p-4 text-sm font-medium text-gray-900">Fee Balance</td>
                        <td className="p-4 text-sm text-right text-gray-700 font-semibold">{USDollar.format(activeTotals.fee_balance || 0)}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          ) : data && clientFilteredRows.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead className="sticky top-0 bg-white shadow-xs">
                  <tr className="bg-gray-100 border-b-2 border-gray-300">
                    <th className="text-left p-3 text-sm font-semibold text-gray-700">Invoice</th>
                    <th className="text-left p-3 text-sm font-semibold text-gray-700">Issuer</th>
                    {showBranchColumn && !isMobile && <th className="text-left p-3 text-sm font-semibold text-gray-700">Branch</th>}
                    <th className="text-left p-3 text-sm font-semibold text-gray-700">Due Date</th>
                    <th className="text-right p-3 text-sm font-semibold text-gray-700">Amount</th>
                    <th className="text-right p-3 text-sm font-semibold text-gray-700">Discount</th>
                    <th className="text-right p-3 text-sm font-semibold text-gray-700">Net</th>
                    <th className="text-right p-3 text-sm font-semibold text-gray-700">Fee</th>
                    {activeTab === "partial_paid" && <th className="text-right p-3 text-sm font-semibold text-gray-700">Paid</th>}
                    <th className="text-right p-3 text-sm font-semibold text-gray-700">Total</th>
                    <th className="text-right p-3 text-sm font-semibold text-gray-700">Net Balance</th>
                    <th className="text-right p-3 text-sm font-semibold text-gray-700">Fee Balance</th>
                    {!isMobile && <th className="text-center p-3 text-sm font-semibold text-gray-700">Status</th>}
                  </tr>
                </thead>
                <tbody>
                  {pageSlice.map((row, idx) => (
                    <tr
                      key={row.id ?? `${row.invoice_number}-${idx}`}
                      className={`border-b hover:bg-green-50 transition-colors ${idx % 2 === 0 ? "bg-white" : "bg-gray-50"}`}
                    >
                      <td className="p-3 text-sm font-medium text-gray-900">{row.invoice_number}</td>
                      <td className="p-3 text-sm text-gray-700">{row.issuer?.name || "-"}</td>
                      {showBranchColumn && !isMobile && <td className="p-3 text-sm text-gray-700">{row.filial?.name || "-"}</td>}
                      <td className="p-3 text-sm text-gray-700">{formatDate(row.due_date)}</td>
                      <td className="p-3 text-sm text-right text-gray-900">{USDollar.format(row.amount || 0)}</td>
                      <td className="p-3 text-sm text-right text-red-600">{row.discount ? `(${USDollar.format(row.discount)})` : "-"}</td>
                      <td className="p-3 text-sm text-right font-semibold text-gray-900">{USDollar.format(row.net || 0)}</td>
                      <td className="p-3 text-sm text-right text-gray-900">{row.fee ? USDollar.format(row.fee) : "-"}</td>
                      {activeTab === "partial_paid" && (
                        <td className="p-3 text-sm text-right text-green-700 font-semibold">{USDollar.format(row.paid || 0)}</td>
                      )}
                      <td className="p-3 text-sm text-right text-gray-900">{USDollar.format(row.total)}</td>
                      <td className="p-3 text-sm text-right font-semibold text-gray-900">{USDollar.format(row.net_balance ?? row.balance ?? 0)}</td>
                      <td className="p-3 text-sm text-right text-gray-700">{USDollar.format(row.fee_balance || 0)}</td>
                      {!isMobile && (
                        <td className="p-3 text-sm text-center">
                          <span className="inline-block px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs font-medium">
                            {row.status}
                          </span>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="flex items-center justify-center h-64 text-gray-500">
              {debouncedTerm
                ? `No results found for "${debouncedTerm}"`
                : activeTab === "partial_paid"
                ? "No partially paid receivables found for this period"
                : "No overdue receivables found for this period"}
            </div>
          )}
        </div>

        {activeTab !== "breakdown" && totalRowsUI > 0 && (
          <div className="flex items-center justify-between p-4 border-t bg-gray-50">
            <div className="text-sm text-gray-600">Page {currentPage} of {totalPages}</div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1 || loading}
                className="p-2 rounded-lg border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors bg-white"
              >
                <ChevronLeft size={20} />
              </button>
              <span className="text-sm px-3 font-medium text-gray-700">
                {currentPage} / {totalPages}
              </span>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages || loading}
                className="p-2 rounded-lg border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors bg-white"
              >
                <ChevronRight size={20} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function OverdueBreakdown({ breakdown, overdue }) {
  const pending = breakdown.pending || {};
  const partial = breakdown.partialPaid || {};
  const pendingPct = Number(pending.pct_of_overdue ?? 0);
  const partialPct = Number(partial.pct_of_overdue ?? 0);

  const PENDING_COLOR = "#f59e0b"; // amber-500
  const PARTIAL_COLOR = "#f97316"; // orange-500
  const R = 15.9155;
  const CIRC = 2 * Math.PI * R;
  const pendingDash = (pendingPct / 100) * CIRC;
  const partialDash = (partialPct / 100) * CIRC;

  return (
    <div className="h-full overflow-y-auto p-4 sm:p-6">
      <div className="max-w-3xl mx-auto space-y-6">
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-3">
            Why {USDollar.format(overdue || 0)} is overdue
          </h3>
          <div className="flex items-center gap-6">
            <svg viewBox="0 0 36 36" className="w-24 h-24 shrink-0 -rotate-90">
              <circle cx="18" cy="18" r={R} fill="none" stroke="#e5e7eb" strokeWidth="4" />
              {partialPct > 0 && (
                <circle
                  cx="18" cy="18" r={R} fill="none" stroke={PARTIAL_COLOR} strokeWidth="4"
                  strokeDasharray={`${partialDash} ${CIRC - partialDash}`}
                  strokeDashoffset={-pendingDash}
                  strokeLinecap="round"
                />
              )}
              {pendingPct > 0 && (
                <circle
                  cx="18" cy="18" r={R} fill="none" stroke={PENDING_COLOR} strokeWidth="4"
                  strokeDasharray={`${pendingDash} ${CIRC - pendingDash}`}
                  strokeLinecap="round"
                />
              )}
            </svg>
            <div className="space-y-1.5">
              <p className="flex items-center gap-2 text-sm">
                <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: PENDING_COLOR }} />
                <span className="text-gray-600">Pending:</span>
                <span className="font-semibold text-gray-900">{USDollar.format(pending.net_balance || 0)}</span>
                <span className="text-gray-400">({pendingPct.toFixed(1)}%)</span>
              </p>
              <p className="flex items-center gap-2 text-sm">
                <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: PARTIAL_COLOR }} />
                <span className="text-gray-600">Partial Paid:</span>
                <span className="font-semibold text-gray-900">{USDollar.format(partial.net_balance || 0)}</span>
                <span className="text-gray-400">({partialPct.toFixed(1)}%)</span>
              </p>
              <p className="text-xs text-gray-500 pt-1 border-t mt-1.5">
                Total overdue: <span className="font-semibold text-gray-700">{USDollar.format(overdue || 0)}</span>
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="rounded-lg border border-amber-300 bg-amber-50 p-3">
            <div className="flex items-center gap-2 mb-1">
              <span className="h-2 w-2 rounded-full bg-amber-400 shrink-0" />
              <p className="text-sm font-semibold text-gray-800">Pending</p>
            </div>
            <p className="text-xl font-bold text-gray-900 leading-tight">{USDollar.format(pending.net_balance || 0)}</p>
            <p className="text-xs text-gray-600 mt-0.5">{pendingPct.toFixed(1)}% of overdue • {pending.count ?? 0} receivable(s)</p>
          </div>

          <div className="rounded-lg border border-orange-300 bg-orange-50 p-3">
            <div className="flex items-center gap-2 mb-1">
              <span className="h-2 w-2 rounded-full bg-orange-500 shrink-0" />
              <p className="text-sm font-semibold text-gray-800">Partial Paid</p>
            </div>
            <p className="text-xl font-bold text-gray-900 leading-tight">{USDollar.format(partial.net_balance || 0)}</p>
            <p className="text-xs text-gray-600 mt-0.5">
              {partialPct.toFixed(1)}% of overdue • {partial.count ?? 0} receivable(s) • {USDollar.format(partial.paid || 0)} already paid
            </p>
          </div>
        </div>

        <div className="rounded-lg border border-gray-200 bg-white divide-y">
          <div className="p-4">
            <p className="text-sm font-semibold text-gray-800 mb-1">Pending — nothing paid yet</p>
            <p className="text-sm text-gray-600">
              This invoice's debt is <span className="font-medium text-gray-800">Amount − Discount</span> (the "Net" value).
              Since the customer hasn't made any payment on it yet, the full Net amount is counted as overdue.
            </p>
          </div>
          <div className="p-4">
            <p className="text-sm font-semibold text-gray-800 mb-1">Partial Paid — customer paid something, but not enough</p>
            <p className="text-sm text-gray-600 mb-2">
              We add up every payment the customer has made on this invoice (ignoring any payment that was later canceled) to get the total <span className="font-medium">Paid</span> amount.
            </p>
            <p className="text-sm text-gray-600 mb-2">
              That amount is used to pay off the <span className="font-medium">Net</span> (the service/product value) <span className="font-medium">first</span>.
              Only once the Net is fully covered does any leftover payment start paying off the Fee. So:
            </p>
            <ul className="text-sm text-gray-600 list-disc list-inside space-y-1">
              <li><span className="font-medium">Net balance</span> is how much of the Net is still unpaid (Net minus Paid, never less than $0) — <span className="text-gray-800">this is what still counts as overdue</span>.</li>
              <li><span className="font-medium">Fee balance</span> is how much of the Fee is still unpaid after the Net is covered — this does <span className="font-medium">not</span> count as overdue.</li>
            </ul>
            <p className="text-sm text-gray-600 mt-2">
              In other words: as soon as the Net balance reaches $0, the invoice is no longer delinquent — even if the Fee hasn't been paid in full yet.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
